"use client";

import { motion, useScroll, useTransform, type Variants } from "motion/react";
import { useInView } from "react-intersection-observer";
import { Clock, Video, CalendarCheck, Calendar } from "lucide-react";
import { useEffect, useRef } from "react";

export default function CTA() {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const sectionRef = useRef(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const opacity = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    [0, 1, 1, 0.8]
  );
  const scale = useTransform(scrollYProgress, [0, 0.3], [0.9, 1]);

  const benefits = [
    {
      icon: <Clock className="w-5 h-5" />,
      title: "5 minutes",
      subtitle: "Durée de l'appel",
    },
    {
      icon: <Video className="w-5 h-5" />,
      title: "Visioconférence",
      subtitle: "100% en ligne",
    },
    {
      icon: <CalendarCheck className="w-5 h-5" />,
      title: "Sans engagement",
      subtitle: "Gratuit et sans obligation",
    },
  ];

  // Variants
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.05,
      },
    },
  };

  const titleVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring" as const,
        damping: 20,
        stiffness: 100,
        duration: 0.8,
      },
    },
  };

  const benefitVariants: Variants = {
    hidden: {
      opacity: 0,
      x: -50,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 18,
        stiffness: 160,
        duration: 0.35,
      },
    },
  };

  const calendarVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 80,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring" as const,
        damping: 25,
        stiffness: 100,
        duration: 1,
      },
    },
  };

  return (
    <section ref={sectionRef} className="py-20 bg-white overflow-hidden">
      <motion.div
        ref={ref}
        style={{ opacity, scale }}
        className="section-container"
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-12"
        >
          <motion.h2
            variants={titleVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
          >
            5 minutes suffisent pour tout comprendre
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-gray-600 max-w-2xl mx-auto"
          >
            Réservez directement votre visioconférence avec notre équipe via le
            calendrier ci-dessous.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-3 gap-6 mb-12"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={benefitVariants}
              whileHover={{
                y: -5,
                scale: 1.05,
                transition: {
                  type: "spring" as const,
                  stiffness: 300,
                  damping: 20,
                },
              }}
              className="group flex items-center gap-3 bg-card rounded-xl p-4 border border-primary-100/50 hover:shadow-lg hover:border-primary-200/80 transition-all duration-300"
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={inView ? { scale: 1, rotate: 0 } : {}}
                transition={{
                  delay: 0.1 + index * 0.05,
                  type: "spring",
                  stiffness: 200,
                  damping: 15,
                }}
                whileHover={{
                  rotate: [0, -10, 10, 0],
                  transition: { duration: 0.4 },
                }}
                className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center app-icon relative"
              >
                <motion.div
                  className="absolute inset-0 rounded-lg bg-primary-400/20 blur-lg"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 0.8, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <div className="relative z-10">{benefit.icon}</div>
              </motion.div>

              <div>
                <div className="font-semibold text-gray-900">
                  {benefit.title}
                </div>
                <div className="text-sm text-gray-600">{benefit.subtitle}</div>
              </div>

              <motion.div
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.3 }}
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-400 to-primary-500 rounded-b-xl origin-left"
              />
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          variants={calendarVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
          className="bg-gradient-to-br from-gray-50 to-primary-50/30 rounded-2xl p-12 border-2 border-dashed border-primary-600/15 shadow-sm hover:shadow-xl transition-shadow duration-300"
        >
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <motion.div
              animate={{
                y: [0, -10, 0],
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Calendar className="w-16 h-16 text-primary-500 mx-auto mb-6" />
            </motion.div>

            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Prise de rendez-vous en quelques secondes
            </h3>
            <div
              className="calendly-inline-widget flex justify-center px-0 md:px-6 py-3 bg-white border-2 border-dashed border-gray-300 rounded-lg text-gray-500 text-sm"
              data-url="https://calendly.com/contact-formly-app/30min?hide_gdpr_banner=1"
              style={{ minWidth: "320px", height: "700px" }}
            ></div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}
